import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';
import { inventoryService } from '../../services/inventoryService';

import Header from '../../components/navigation/Header';
import MobileNavigation from '../../components/navigation/MobileNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import ShoppingListItem from './components/ShoppingListItem';
import { useNavigate } from 'react-router-dom';

const ShoppingList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTrip, setActiveTrip] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [sortBy, setSortBy] = useState('urgency');
  const [filterCategory, setFilterCategory] = useState('');
  const [trip, setTrip] = useState(null);
  const [shoppingItems, setShoppingItems] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        const [tripResult, inventoryResult] = await Promise.all([
          tripService?.getActiveTrip(user?.id),
          inventoryService?.getInventoryItems(user?.id)
        ]);
      } catch (e) {
        setError(e?.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const calculatePriority = (quantity, daysRemaining) => {
    const daysPerUnit = daysRemaining / (quantity || 1);
    
    if (quantity < 2 || daysPerUnit > 5) return { level: 'critical', score: 4 };
    if (quantity < 5 || daysPerUnit > 3) return { level: 'high', score: 3 };
    if (quantity < 10 || daysPerUnit > 2) return { level: 'medium', score: 2 };
    return { level: 'low', score: 1 };
  };

  const shoppingListItems = useMemo(() => {
    if (!activeTrip) return [];

    const daysRemaining = activeTrip?.duration || 0;
    
    const lowStockItems = inventory?.filter(item => item?.quantity < 10)?.map(item => {
      const priority = calculatePriority(item?.quantity, daysRemaining);
      
      return {
        id: item?.id,
        name: item?.name,
        category: item?.category,
        currentQuantity: item?.quantity,
        unit: item?.unit,
        daysRemaining,
        priority: priority?.level,
        priorityScore: priority?.score,
        suggestedQuantity: Math.max(daysRemaining - item?.quantity, 5)
      };
    });

    return lowStockItems || [];
  }, [inventory, activeTrip]);

  const sortedAndFilteredItems = useMemo(() => {
    let items = [...shoppingListItems];

    if (filterCategory) {
      items = items?.filter(item => item?.category === filterCategory);
    }

    items?.sort((a, b) => {
      if (sortBy === 'urgency') {
        if (b?.priorityScore !== a?.priorityScore) {
          return b?.priorityScore - a?.priorityScore;
        }
        return a?.currentQuantity - b?.currentQuantity;
      } else if (sortBy === 'category') {
        return a?.category?.localeCompare(b?.category);
      } else if (sortBy === 'name') {
        return a?.name?.localeCompare(b?.name);
      }
      return 0;
    });

    return items;
  }, [shoppingListItems, sortBy, filterCategory]);

  const groupedByCategory = useMemo(() => {
    const grouped = {};
    sortedAndFilteredItems?.forEach(item => {
      if (!grouped?.[item?.category]) {
        grouped[item?.category] = [];
      }
      grouped?.[item?.category]?.push(item);
    });
    return grouped;
  }, [sortedAndFilteredItems]);

  const stats = useMemo(() => {
    const critical = shoppingListItems?.filter(item => item?.priority === 'critical')?.length;
    const high = shoppingListItems?.filter(item => item?.priority === 'high')?.length;
    const medium = shoppingListItems?.filter(item => item?.priority === 'medium')?.length;
    
    return {
      total: shoppingListItems?.length,
      critical,
      high,
      medium
    };
  }, [shoppingListItems]);

  const categories = ['Fresh', 'Frozen', 'Canned', 'Dry', 'Snack', 'Condiment', 'Beverage', 'Other'];

  const sortOptions = [
    { value: 'urgency', label: 'Sort by Urgency' },
    { value: 'category', label: 'Sort by Category' },
    { value: 'name', label: 'Sort by Name' }
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...categories?.map(cat => ({ value: cat, label: cat }))
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading shopping list...</p>
        </div>
      </div>
    );
  }

  if (!activeTrip) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Shopping List - GalleyPlan</title>
        </Helmet>
        <Header />
        <main className="main-content">
          <div className="max-w-4xl mx-auto text-center py-12">
            <Icon name="ShoppingCart" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">No Active Trip</h2>
            <p className="text-muted-foreground mb-6">Create a trip to generate your shopping list.</p>
            <Button onClick={() => window.location.href = '/trip-setup'}>Create Trip</Button>
          </div>
        </main>
        <MobileNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Shopping List - GalleyPlan</title>
      </Helmet>
      <Header />
      <main className="main-content pb-20 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          {/* Header Section */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="ShoppingCart" size={32} className="text-primary" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                Shopping List
              </h1>
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              Prioritized by urgency and remaining trip days ({activeTrip?.duration} days remaining)
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ListChecks" size={20} className="text-primary" />
                <span className="text-xs md:text-sm text-muted-foreground">Total Items</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground">{stats?.total}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
                <span className="text-xs md:text-sm text-muted-foreground">Critical</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-destructive">{stats?.critical}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={20} className="text-warning" />
                <span className="text-xs md:text-sm text-muted-foreground">High</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-warning">{stats?.high}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={20} className="text-secondary" />
                <span className="text-xs md:text-sm text-muted-foreground">Medium</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-secondary">{stats?.medium}</p>
            </div>
          </div>

          {/* Filters and Sort */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                value={sortBy}
                onChange={(val) => setSortBy(val)}
                options={sortOptions}
                placeholder="Sort by..."
              />
              <Select
                value={filterCategory}
                onChange={(val) => setFilterCategory(val)}
                options={categoryOptions}
                placeholder="Filter by category..."
              />
            </div>
          </div>

          {/* Shopping List Items */}
          {sortedAndFilteredItems?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="CheckCircle2" size={64} className="text-success mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">All Stocked Up!</h3>
              <p className="text-muted-foreground">No items need restocking at this time.</p>
            </div>
          ) : sortBy === 'category' ? (
            <div className="space-y-6">
              {Object?.entries(groupedByCategory)?.map(([category, items]) => (
                <div key={category} className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="bg-muted px-4 py-3 border-b border-border">
                    <h3 className="text-lg font-semibold text-foreground">{category}</h3>
                    <p className="text-sm text-muted-foreground">{items?.length} items</p>
                  </div>
                  <div className="divide-y divide-border">
                    {items?.map(item => (
                      <ShoppingListItem key={item?.id} item={item} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="divide-y divide-border">
                {sortedAndFilteredItems?.map(item => (
                  <ShoppingListItem key={item?.id} item={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <MobileNavigation />
    </div>
  );
};

export default ShoppingList;